'use client';

import * as React from 'react';

import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';

import { insightCardVariants } from './insight-card.variants';

type InsightCardDivProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children' | 'style'
>;

type InsightCardSharedProps = InsightCardDivProps & {
  /**
   * Surface + border style (same concept as `DataPoint`’s `style` prop; `style` is not
   * the React inline `style` attribute—use `className` for one-off layout tweaks).
   */
  style?: 'primary' | 'secondary' | 'neutral';
  /** Optional trailing visual (e.g. `CircularProgress`). */
  trailing?: React.ReactNode;
};

/**
 * Use `title` and/or `description` for the simple API, or `children` for a custom body — not both.
 */
export type InsightCardProps = InsightCardSharedProps &
  (
    | {
        /** Title when using the simple prop API. */
        title?: string;
        /** Description when using the simple prop API. */
        description?: React.ReactNode;
        children?: never;
      }
    | {
        title?: never;
        description?: never;
        /**
         * Rich body content. Mutually exclusive with `title` / `description` at the type level.
         * A one-time dev warning still runs if both are passed at runtime (e.g. untyped callers).
         */
        children?: React.ReactNode;
      }
  );

export const InsightCard = React.forwardRef<HTMLDivElement, InsightCardProps>(
  (
    {
      style: surfaceStyle = 'primary',
      title,
      description,
      trailing,
      children,
      className,
      role,
      ...rest
    },
    ref,
  ) => {
    const visibleChildren = React.Children.toArray(children).filter(
      (child) => child != null && typeof child !== 'boolean',
    );
    const hasChildren = visibleChildren.length > 0;
    const hasPropBody = title != null || description != null;
    const warnedInvalidBodyRef = React.useRef(false);

    if (
      process.env.NODE_ENV !== 'production' &&
      hasChildren &&
      hasPropBody &&
      !warnedInvalidBodyRef.current
    ) {
      warnedInvalidBodyRef.current = true;
      console.warn(
        '[hen-ui/InsightCard] Use either `children` or `title`/`description`, not both. Rendering `children` only.',
      );
    }

    const usePropBody = hasPropBody && !hasChildren;
    const ariaLabelProp = rest['aria-label'];
    const ariaLabelledByProp = rest['aria-labelledby'];
    const hasRegionAccessibleName =
      (typeof ariaLabelProp === 'string' && ariaLabelProp.trim() !== '') ||
      (typeof ariaLabelledByProp === 'string' &&
        ariaLabelledByProp.trim() !== '');

    return (
      <Flex
        ref={ref}
        direction='column'
        gap='md'
        align='stretch'
        width='full'
        className={cn(
          className,
          insightCardVariants({ style: surfaceStyle }),
          'sm:flex-row sm:items-start',
        )}
        role={role ?? (hasRegionAccessibleName ? 'region' : undefined)}
        {...rest}
      >
        <Flex
          direction='column'
          gap='xs'
          align='start'
          className='min-w-0 flex-1'
        >
          {usePropBody ? (
            <>
              {title ? <Heading variant='h5'>{title}</Heading> : null}
              {description ? (
                <Text
                  variant='b5'
                  color='subtext'
                  tag='div'
                  className='min-w-0'
                >
                  {description}
                </Text>
              ) : null}
            </>
          ) : (
            children
          )}
        </Flex>

        {trailing ? (
          <Flex
            direction='row'
            justify='center'
            className='w-full shrink-0 sm:w-auto sm:justify-end'
          >
            {trailing}
          </Flex>
        ) : null}
      </Flex>
    );
  },
);

InsightCard.displayName = 'InsightCard';
